<template>
  <div class="flex flex-wrap gap-2">
    <!-- Layout responsive: si hay vencidas mostramos dos columnas, si no una sola columna -->
    <div
      v-if="expiredActiveAlertas.length > 0"
      class="w-full grid gap-4 md:grid-cols-2 items-start mb-3"
    >
      <!-- Columna izquierda: alertas activas normales -->
      <div>
        <div v-if="showTitle" class="flex items-center justify-between mb-2">
          <div class="flex items-center">
            <ExclamationTriangleIcon class="size-5 text-amber-500 dark:text-amber-400 mr-2" />
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Alertas del Paciente</h3>
          </div>
          <div class="ml-2">
            <slot name="actions" />
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <div v-for="(alert, index) in activeAlertas" :key="`active-${index}`" class="relative inline-flex">
            <div v-if="alert.alerEstado === 1">
              <div
                :ref="el => { const resolved = (el && (el as any).$el) ? (el as any).$el : el; triggers[`active-${index}`] = resolved as Element | null }"
                :class="[
                  'cursor-pointer px-2.5 py-1 rounded-md text-xs font-medium hover:ring-2 transition-shadow',
                  `bg-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-50`,
                  `text-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-700`,
                  `ring-1 ring-inset ring-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-600/20`,
                  `dark:bg-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-900/10`,
                  `dark:text-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-500`,
                  `dark:ring-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-400/30`
                ]"
                @mouseenter="(e) => showTooltip(e, `active-${index}`, alert)"
              @mouseleave="hideTooltip"
              @click="abrirDetalle(alert)"
              >
                {{ getAlertNombre(alert) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Caso sin alertas vencidas: ocupar todo el ancho con título y alertas -->
    <div v-else class="w-full mb-3">
      <div v-if="showTitle" class="flex items-center justify-between mb-2">
        <div class="flex items-center">
          <ExclamationTriangleIcon class="size-5 text-amber-500 dark:text-amber-400 mr-2" />
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Alertas del Paciente</h3>
        </div>
        <div class="ml-2">
          <slot name="actions" />
        </div>
      </div>
      <div class="flex flex-wrap gap-2">
        <div v-for="(alert, index) in activeAlertas" :key="`active-${index}`" class="relative inline-flex">
          <div v-if="alert.alerEstado === 1">
            <div
              :ref="el => { const resolved = (el && (el as any).$el) ? (el as any).$el : el; triggers[`active-${index}`] = resolved as Element | null }"
              :class="[
                'cursor-pointer px-2.5 py-1 rounded-md text-xs font-medium hover:ring-2 transition-shadow',
                `bg-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-50`,
                `text-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-700`,
                `ring-1 ring-inset ring-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-600/20`,
                `dark:bg-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-900/10`,
                `dark:text-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-500`,
                `dark:ring-${getAlertStyle(alert.tipoAlerta?.tipo?.comaNombrecorto as string).color}-400/30`
              ]"
              @mouseenter="(e) => showTooltip(e, `active-${index}`, alert)"
              @mouseleave="hideTooltip"
              @click="abrirDetalle(alert)"
            >
              {{ getAlertNombre(alert) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tooltip: render in body and show current alert info -->
    <teleport to="body">
      <div
        v-if="tooltip.visible && tooltip.currentAlert"
        :style="tooltip.style"
        class="fixed z-[60] bg-gray-900 text-white text-sm rounded-lg py-2 px-3 mb-2 shadow-lg dark:bg-gray-700 max-w-[300px]"
        role="tooltip"
        aria-hidden="false"
      >
        <div class="space-y-1">
          <p class="font-medium">
            {{ getAlertStyle(tooltip.currentAlert?.tipoAlerta?.tipo?.comaNombrecorto as string).title }}
          </p>
          <p v-if="tooltip.currentAlert?.alerDetalle" class="text-gray-200 dark:text-gray-300">
            Descripción: {{ tooltip.currentAlert?.alerDetalle }}
          </p>
          <!-- Servicio info -->
          <div v-if="tooltip.currentAlert?.servicio" class="pt-1 border-t border-gray-600 dark:border-gray-500 mt-1">
            <p class="text-xs text-gray-300 dark:text-gray-400 font-medium mb-0.5">SERVICIO:</p>
            <p class="text-xs font-semibold text-white">{{ tooltip.currentAlert.servicio.coseNombre }}</p>
          </div>
          <p v-if="tooltip.currentAlert?.alerFecha" class="text-xs text-gray-200 dark:text-gray-300">
            Fecha de vencimiento: {{ formatDate(tooltip.currentAlert?.alerFecha) }}
          </p>
          <p class="text-xs text-gray-300 dark:text-gray-400">
            {{ formatDate(tooltip.currentAlert?.createdAt as string) }}
          </p>
        </div>
      </div>
    </teleport>

    <!-- Modal con detalles + botón cerrar alerta -->
    <ModalDefault
      :id="detalleModalId"
      title="Detalle de alerta"
      size="sm"
      :button="false"
      header-color="blue"
    >
      <template #modal-body>
        <div v-if="selectedAlerta" class="px-2 py-2">
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ getAlertNombre(selectedAlerta) }}
          </p>
          <p v-if="selectedAlerta.alerDetalle" class="mt-1 text-sm text-gray-600 dark:text-neutral-300">
            {{ selectedAlerta.alerDetalle }}
          </p>
          <div class="mt-3 space-y-1 text-xs text-gray-500 dark:text-neutral-400">
            <p v-if="selectedAlerta.servicio">
              <span class="font-medium text-gray-600 dark:text-neutral-300">Servicio:</span> {{ selectedAlerta.servicio.coseNombre }}
            </p>
            <p v-if="selectedAlerta.alerFecha">
              <span class="font-medium text-gray-600 dark:text-neutral-300">Vence el:</span> {{ formatDate(selectedAlerta.alerFecha) }}
            </p>
            <p>
              <span class="font-medium text-gray-600 dark:text-neutral-300">Registrada el:</span> {{ formatDate(selectedAlerta.createdAt as string) }}
            </p>
          </div>
          <p v-if="selectedAlerta.codoId" class="mt-3 text-xs text-indigo-600 dark:text-indigo-400">
            Al cerrar esta alerta se cargará el formulario configurado.
          </p>
        </div>
      </template>
      <template #modal-footer>
        <ButtonDefault color="white" @click="closeModal(detalleModalId)">Cerrar</ButtonDefault>
        <BtnCerrarAlerta
          v-if="showCloseButton && selectedAlerta && selectedAlerta.alerEstado === 1"
          :alerta="selectedAlerta"
          :cmot-tipo="cmotTipo"
          :citt-id-context="cittIdContext"
          label="Cerrar alerta"
          @closed="handleAlertaCerrada"
        />
      </template>
    </ModalDefault>
  </div>
</template>

<script setup lang="ts">
import { useAlerts, splitAlertasByFecha } from '../../composables/useAlerts'
import { toRef } from 'vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import type { IAlertAlertas } from '@/modules/Asistencial/interfaces'
import { reactive, onBeforeUnmount, computed, ref } from 'vue'
import ModalDefault from '@/modules/Core/components/Layout/ModalDefault.vue'
import ButtonDefault from '@/modules/Core/components/general/ButtonDefault.vue'
import BtnCerrarAlerta from '@/modules/DemandaInducida/Components/BtnCerrarAlerta.vue'
import { openModal, closeModal } from '@/modules/Core/usings/generales'

interface Props {
  coppId: number
  alertas: IAlertAlertas[]
  filterByFecha?: boolean,
  showTitle?: boolean
  showCloseButton?: boolean
  cmotTipo?: string
  cittIdContext?: number
}

const props = withDefaults(defineProps<Props>(), {
  showCloseButton: true,
  cmotTipo: 'CIERRE ALERTA',
  cittIdContext: undefined,
})

const emit = defineEmits<{
  (e: 'alerta-cerrada'): void
}>()

const detalleModalId = 'modal-alert-mini-detalle'
const selectedAlerta = ref<IAlertAlertas | null>(null)

const abrirDetalle = (alert: IAlertAlertas) => {
  selectedAlerta.value = alert
  openModal(detalleModalId)
}

const handleAlertaCerrada = () => {
  closeModal(detalleModalId)
  selectedAlerta.value = null
  emit('alerta-cerrada')
}

// Usar solo las funciones necesarias del composable
const { getAlertStyle, formatDate, getAlertNombre } = useAlerts({
  coppId: toRef(props, 'coppId')
})


// tooltip reactive state
const tooltip = reactive({
  visible: false,
  alertIndex: null as string | null,
  currentAlert: null as IAlertAlertas | null,
  style: {
    left: '0px',
    top: '0px',
    transform: 'translateX(-50%) translateY(-100%)'
  }
})

// Dividir alertas activas y vencidas activas para mostrar secciones separadas
const split = computed(() => splitAlertasByFecha(props.alertas, props.filterByFecha ?? true))
const activeAlertas = computed(() => split.value.active)
const expiredActiveAlertas = computed(() => split.value.expiredActive)

// store trigger elements by key. Use a mapping porque ahora usamos keys string
const triggers: Record<string, Element | null> = {}

function showTooltip(event: MouseEvent, key: string, alert?: IAlertAlertas) {
  // Try to resolve element from our triggers map; fallback to event.currentTarget
  const trigger = triggers[key] || (event.currentTarget as Element)
  if (!trigger) return

  const el = (trigger as Element & { getBoundingClientRect?: () => DOMRect })
  if (typeof el.getBoundingClientRect !== 'function') return
  const rect = el.getBoundingClientRect()

  const tooltipMaxW = 300
  const margin = 8

  // starting center x
  let left = rect.left + rect.width / 2
  // position above the trigger
  const top = rect.top - margin

  const half = tooltipMaxW / 2
  const vw = window.innerWidth
  // adjust to avoid clipping left/right
  if (left - half < 8) left = 8 + half
  if (left + half > vw - 8) left = vw - 8 - half

  tooltip.style.left = `${left}px`
  tooltip.style.top = `${top}px`
  tooltip.visible = true
  tooltip.alertIndex = key
  tooltip.currentAlert = alert ?? null
}

function hideTooltip() {
  tooltip.visible = false
  tooltip.alertIndex = null
  tooltip.currentAlert = null
}

onBeforeUnmount(() => {
  Object.keys(triggers).forEach(k => delete triggers[k])
})
</script>